import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { KeyRound, MailCheck, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../api';
import { formatApiErrorDetail } from './Login';

const Shell = ({ children, testId }) => (
  <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 flex items-center justify-center px-4 py-24">
    <Card className="w-full max-w-md border-0 shadow-2xl" data-testid={testId}>
      <CardContent className="p-8">{children}</CardContent>
    </Card>
  </div>
);

export const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Shell testId="forgot-password-page">
      <Link to="/login" className="inline-flex items-center text-sm text-teal-600 hover:underline mb-6"><ArrowLeft className="w-4 h-4 mr-1" /> Back to login</Link>
      {sent ? (
        <div className="text-center" data-testid="forgot-password-sent">
          <MailCheck className="w-14 h-14 text-teal-600 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Check your inbox</h1>
          <p className="text-gray-600">If an account exists for <strong>{email}</strong>, we've emailed a link to reset your password. The link expires in 1 hour.</p>
        </div>
      ) : (
        <>
          <div className="text-center mb-6">
            <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4"><KeyRound className="w-7 h-7 text-white" /></div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Forgot your password?</h1>
            <p className="text-gray-600">Enter your email and we'll send you a reset link.</p>
          </div>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="mb-1.5 block">Email</Label>
              <Input data-testid="forgot-email-input" id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="border-2" placeholder="you@example.com" />
            </div>
            <Button data-testid="forgot-submit-btn" type="submit" disabled={submitting} className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 text-white py-5">
              {submitting ? 'Sending...' : 'Send reset link'}
            </Button>
          </form>
        </>
      )}
    </Shell>
  );
};

export const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      toast.success('Password updated. Please log in.');
      navigate('/login');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Shell testId="reset-password-page">
      <div className="text-center mb-6">
        <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4"><KeyRound className="w-7 h-7 text-white" /></div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Choose a new password</h1>
      </div>
      {!token ? (
        <p data-testid="reset-missing-token" className="text-center text-red-600">This reset link is invalid. <Link to="/forgot-password" className="underline">Request a new one</Link>.</p>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="password" className="mb-1.5 block">New password</Label>
            <Input data-testid="reset-password-input" id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} className="border-2" placeholder="At least 8 characters" autoComplete="new-password" />
          </div>
          <div>
            <Label htmlFor="confirm" className="mb-1.5 block">Confirm password</Label>
            <Input data-testid="reset-confirm-input" id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8} className="border-2" autoComplete="new-password" />
          </div>
          {error && <p data-testid="reset-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">{error}</p>}
          <Button data-testid="reset-submit-btn" type="submit" disabled={submitting} className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 text-white py-5">
            {submitting ? 'Updating...' : 'Update password'}
          </Button>
        </form>
      )}
    </Shell>
  );
};
